export const SUPPORTED_ADVERTISER_VERTICALS = [
  "general",
  "film",
  "automotive",
  "retail",
  "travel_hospitality",
  "financial_services",
  "crypto_web3",
  "health_wellness",
  "healthcare_public_health",
  "education",
  "b2b_saas",
  "nonprofit_public_sector",
  "real_estate",
  "food_beverage",
  "cpg",
  "gaming",
  "telecom",
  "electronics",
];

export const ADVERTISER_VERTICAL_OPTIONS = [
  { value: "general", label: "General" },
  { value: "automotive", label: "Automotive" },
  { value: "b2b_saas", label: "B2B / SaaS" },
  { value: "crypto_web3", label: "Blockchain / Crypto / Web3" },
  { value: "cpg", label: "CPG" },
  { value: "education", label: "Education" },
  { value: "electronics", label: "Electronics" },
  { value: "film", label: "Film / Entertainment" },
  { value: "financial_services", label: "Financial Services" },
  { value: "food_beverage", label: "Food / Beverage / Restaurants" },
  { value: "gaming", label: "Gaming" },
  { value: "health_wellness", label: "Health / Wellness" },
  { value: "healthcare_public_health", label: "Healthcare / Public Health" },
  { value: "nonprofit_public_sector", label: "Nonprofit / Public Sector" },
  { value: "real_estate", label: "Real Estate" },
  { value: "retail", label: "Retail / E-Commerce" },
  { value: "telecom", label: "Telecom" },
  { value: "travel_hospitality", label: "Travel / Hospitality" },
];

// Entertainment terms every non-film vertical must avoid (film is the locked
// Arts & Entertainment control vertical, where this language is native).
const ENTERTAINMENT_BANNED_BASE = [
  "box office",
  "ticket sales",
  "showtimes",
  "moviegoers",
  "theatrical release",
  "cinema market",
  "opening weekend",
];

export function normalizeAdvertiserVertical(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (normalized === "auto") return "automotive";
  if (
    [
      "travel",
      "hospitality",
      "travel_hospitality",
      "travel_and_hospitality",
      "travel_tourism",
      "tourism",
      "hotel",
      "hotels",
      "resort",
      "resorts",
      "destination",
      "destinations",
      "airline",
      "airlines",
    ].includes(normalized)
  ) {
    return "travel_hospitality";
  }
  if (
    [
      "retail_e_commerce",
      "retail_ecommerce",
      "e_commerce",
      "ecommerce",
      "commerce",
      "ecomm",
    ].includes(normalized)
  ) {
    return "retail";
  }
  if (normalized === "entertainment") return "film";
  if (["finance", "financial", "banking", "bank", "fintech", "insurance", "credit_union", "wealth_management"].includes(normalized)) {
    return "financial_services";
  }
  if (["crypto", "cryptocurrency", "blockchain", "web3", "web_3", "defi", "crypto_web_3", "blockchain_crypto_web3", "digital_assets", "nft"].includes(normalized)) {
    return "crypto_web3";
  }
  if (["wellness", "fitness", "gym", "gyms", "spa", "spas", "yoga", "health_and_wellness", "health_wellness", "health"].includes(normalized)) {
    return "health_wellness";
  }
  if (["healthcare", "health_care", "public_health", "healthcare_public_health", "hospital", "hospitals", "medical", "clinic", "clinics", "pharma", "pharmaceutical"].includes(normalized)) {
    return "healthcare_public_health";
  }
  if (["edu", "education", "higher_education", "higher_ed", "university", "universities", "college", "colleges", "school", "schools", "k12", "k_12"].includes(normalized)) {
    return "education";
  }
  if (["b2b", "saas", "b2b_saas", "software", "b2b_software", "enterprise_software", "enterprise", "tech_b2b"].includes(normalized)) {
    return "b2b_saas";
  }
  if (["nonprofit", "non_profit", "nonprofits", "ngo", "charity", "charities", "public_sector", "government", "civic", "nonprofit_public_sector"].includes(normalized)) {
    return "nonprofit_public_sector";
  }
  if (["realestate", "real_estate", "property", "properties", "realtor", "realtors", "brokerage", "homebuilder", "home_builder"].includes(normalized)) {
    return "real_estate";
  }
  if (["food", "beverage", "food_beverage", "food_and_beverage", "restaurant", "restaurants", "qsr", "dining", "cafe", "cafes", "bar", "bars", "food_beverage_restaurants"].includes(normalized)) {
    return "food_beverage";
  }
  if (["cpg", "consumer_packaged_goods", "fmcg", "packaged_goods"].includes(normalized)) {
    return "cpg";
  }
  if (["gaming", "game", "games", "video_game", "video_games", "video_gaming", "esports", "e_sports", "game_studio", "game_publisher", "mobile_gaming", "mobile_games", "console_gaming", "pc_gaming"].includes(normalized)) {
    return "gaming";
  }
  if (["telecom", "telecommunications", "telco", "wireless", "broadband", "isp", "internet_provider", "internet_service_provider", "mobile_carrier", "carrier", "carriers", "fiber", "cable_internet", "5g"].includes(normalized)) {
    return "telecom";
  }
  if (["electronics", "consumer_electronics", "consumer_tech", "consumer_technology", "devices", "hardware", "gadgets", "appliances", "tech_hardware"].includes(normalized)) {
    return "electronics";
  }

  return SUPPORTED_ADVERTISER_VERTICALS.includes(normalized) ? normalized : "general";
}

export function formatAdvertiserVerticalLabel(value) {
  const vertical = normalizeAdvertiserVertical(value);
  return (
    ADVERTISER_VERTICAL_OPTIONS.find((option) => option.value === vertical)?.label ||
    "General"
  );
}

/*
 * ENTERTAINMENT REFERENCE INTELLIGENCE ELIGIBILITY.
 *
 * Mirrors the backend authority in utils/advertiserVerticals.js. IMDb / Rotten
 * Tomatoes ratings and their source cards are native to exactly one governed
 * vertical: film. Every other vertical is ineligible.
 *
 * The portals re-assert the boundary instead of trusting the payload so that a
 * field outside the governed category's schema is never rendered, whatever the
 * payload happens to carry (a stale persisted score, an older backend, a cached
 * response). Advertiser/strategy authority beats semantic guessing: a product
 * name that reads like a title never earns entertainment intelligence.
 */
export const ENTERTAINMENT_REFERENCE_INTELLIGENCE_VERTICALS = ["film"];

export function isEntertainmentReferenceIntelligenceVertical(value) {
  return ENTERTAINMENT_REFERENCE_INTELLIGENCE_VERTICALS.includes(
    normalizeAdvertiserVertical(value)
  );
}

/*
 * BRAND AWARENESS TAXONOMY.
 *
 * The microsite's brand-awareness module is composed in the portal, not sent by
 * the backend, so the backend's vertical language overlay never reached it. The
 * card set was written for theatrical campaigns ("event or screening strategy",
 * "podcast / press circuit") and was emitted for every advertiser regardless of
 * category.
 *
 * AUTHORITY RULE. Theatrical is no longer the implicit default. It requires an
 * affirmative film classification:
 *
 *   declared film     -> FILM taxonomy (theatrical)
 *   declared retail   -> RETAIL taxonomy
 *   any other value   -> GENERAL taxonomy (no entertainment assumptions)
 *   absent / unknown  -> GENERAL taxonomy (fail-closed; see the note below)
 *
 * A declared non-film category can never reach the theatrical set, and neither
 * can an absent one: normalizeAdvertiserVertical() resolves anything it does not
 * recognise to "general", which now maps to the neutral taxonomy rather than the
 * theatrical one. There is no portal path back to theatrical without "film".
 *
 * The renderer reads this table and nothing else, so a specialised vertical
 * taxonomy is added here — one entry in BRAND_AWARENESS_TAXONOMY_BY_VERTICAL —
 * with no renderer change.
 *
 * `signal` selects which campaign-derived signal the page feeds into the card;
 * `lead` is the category's own framing sentence.
 */

/*
 * Entertainment-specific constructs that must never appear in a taxonomy other
 * than the film one. Asserted over card titles AND generated copy by
 * CampaignStrategyMicrositeRetailCategoryBoundary.test.js.
 *
 * Word-anchored on purpose. A bare substring check would fail on "podcast" and
 * "broadcast" (both contain "cast") and on "premises" — a guard that fires on
 * the wrong thing gets deleted by the next person, which is worse than no guard.
 */
export const ENTERTAINMENT_ONLY_TAXONOMY_CONSTRUCTS = [
  "\\bscreenings?\\b",
  "\\btheatrical\\b",
  "\\bopening weekend\\b",
  "\\btrailers?\\b",
  "\\bcast\\b",
  "\\bdirector\\b",
  "\\bfilm festivals?\\b",
  "\\bbox[- ]office\\b",
  "\\brelease geos?\\b",
  "\\bcinemas?\\b",
  "\\bmoviegoers?\\b",
  "\\bshowtimes?\\b",
  "\\bpremieres?\\b",
  "\\bimdb\\b",
  "\\brotten tomatoes\\b",
];

const FILM_BRAND_AWARENESS_TAXONOMY = Object.freeze({
  key: "film",
  cards: Object.freeze([
    {
      key: "narrative-viral-hook",
      title: "Narrative / viral hook",
      meta: "Story signal",
      signal: "narrative",
      lead: (subject) => `Frame ${subject} around the clearest repeatable tension in the campaign.`,
      accent: "rgba(113, 227, 255, 0.94)",
    },
    {
      key: "community-partner-network",
      title: "Community or partner network",
      meta: "Trusted distribution",
      signal: "audience",
      lead: () => "Use partner, audience, creator, and local community nodes as trust carriers.",
      accent: "rgba(184, 126, 255, 0.92)",
    },
    {
      key: "event-screening-strategy",
      title: "Event or screening strategy",
      meta: "Moment design",
      signal: "moment",
      lead: () =>
        "Create moments around screenings, events, launch beats, creator previews, Q&A formats, or local-market activations.",
      accent: "rgba(76, 141, 255, 0.96)",
    },
    {
      key: "podcast-press-circuit",
      title: "Podcast / press circuit",
      meta: "Earned narrative",
      signal: "earned",
      lead: () =>
        "Package the campaign for podcasts, press, newsletters, creators, and culture writers before asking paid media to scale it.",
      accent: "rgba(255, 107, 137, 0.9)",
    },
    {
      key: "social-proof-review-ecosystem",
      title: "Social proof / review ecosystem",
      meta: "Proof loop",
      signal: "socialProof",
      lead: () =>
        "Turn reviews, comments, search behavior, talent signals, and audience response into reusable proof assets.",
      accent: "rgba(63, 224, 160, 0.92)",
    },
    {
      key: "contextual-cultural-angle",
      title: "Contextual cultural angle",
      meta: "Why now",
      signal: "context",
      lead: (subject) => `Connect ${subject} to the cultural context its audience already understands.`,
      accent: "rgba(255, 206, 107, 0.92)",
    },
  ]),
  signalDefaults: Object.freeze({
    narrative: (subject) =>
      `${subject} needs one clear story people can repeat in a sentence, then multiple creative executions that make the hook easy to share.`,
    audience:
      "Priority audiences should see the campaign through trusted voices, relevant communities, and repeated cultural context before paid retargeting asks for action.",
    moment: (subject) =>
      `${subject} should create moments audiences can attend, post about, recap, and revisit through paid retargeting.`,
    earned:
      "Earned media should create credible conversation first; Cphere can then scale the strongest proof through paid creative and retargeting.",
    socialProof:
      "Reviews, comments, talent signals, creator mentions, and site engagement should be treated as proof assets that paid media can reuse.",
    context:
      "Anchor the campaign in the cultural conversation its audience already recognizes, then use paid media to make that angle easier to encounter repeatedly.",
  }),
});

const RETAIL_BRAND_AWARENESS_TAXONOMY = Object.freeze({
  key: "retail",
  cards: Object.freeze([
    {
      key: "brand-product-positioning",
      title: "Brand & product positioning",
      meta: "Positioning signal",
      signal: "narrative",
      lead: (subject) =>
        `State what ${subject} is, who it is for, and what makes it different from the rest of its category in one repeatable line.`,
      accent: "rgba(113, 227, 255, 0.94)",
    },
    {
      key: "shopper-audience-communities",
      title: "Shopper audiences & communities",
      meta: "Trusted distribution",
      signal: "audience",
      lead: () =>
        "Build shopper and customer audiences around purchase intent, category affinity, and the communities that already recommend this kind of product.",
      accent: "rgba(184, 126, 255, 0.92)",
    },
    {
      key: "seasonal-occasion-relevance",
      title: "Seasonal & occasion relevance",
      meta: "Timing design",
      signal: "moment",
      lead: () =>
        "Time pressure to the seasons, occasions, and gifting or replenishment moments when this category is actually bought.",
      accent: "rgba(76, 141, 255, 0.96)",
    },
    {
      key: "creator-affiliate-editorial",
      title: "Creator, affiliate & editorial",
      meta: "Earned narrative",
      signal: "earned",
      lead: () =>
        "Work with creators, affiliates, newsletters, and category editorial so recommendations exist before paid media scales them.",
      accent: "rgba(255, 107, 137, 0.9)",
    },
    {
      key: "reviews-customer-proof",
      title: "Reviews & customer proof",
      meta: "Proof loop",
      signal: "socialProof",
      lead: () =>
        "Treat ratings, reviews, customer photos, and repeat-purchase behaviour as proof assets that paid creative can reuse.",
      accent: "rgba(63, 224, 160, 0.92)",
    },
    {
      key: "purchase-path-retail-footprint",
      title: "Purchase path & retail footprint",
      meta: "Path to purchase",
      signal: "purchasePath",
      lead: () =>
        "Make the next step obvious on every surface — product page, retailer listing, or store market — and measure awareness through to purchase.",
      accent: "rgba(255, 206, 107, 0.92)",
    },
  ]),
  signalDefaults: Object.freeze({
    narrative: (subject) =>
      `${subject} needs one clear product story shoppers can repeat, then creative variants that carry it across discovery, consideration, and purchase.`,
    audience:
      "Shopper audiences should be built from category affinity, search and browse behaviour, and existing customers before paid retargeting asks for a purchase.",
    moment:
      "Seasonal and occasion demand should set the pacing, so pressure lands when the category is being shopped rather than spread evenly across the flight.",
    earned:
      "Creator, affiliate, and editorial coverage should establish credible recommendation first; Cphere can then scale the strongest proof through paid creative and retargeting.",
    socialProof:
      "Ratings, reviews, customer content, and search behaviour should be treated as proof assets that paid media can reuse.",
    purchasePath:
      "Online and physical purchase paths should both be legible, with product-page and retailer traffic measured as the step between awareness and purchase.",
  }),
});

/*
 * The neutral fallback every category without a specialised taxonomy resolves
 * to. It carries no entertainment assumption: no screenings, no theatrical
 * release, no opening weekend, no trailers, no cast or director publicity, no
 * film festivals, no box office, no release geos, no cinema attendance, no
 * entertainment ratings.
 *
 * It is deliberately generic rather than vague — these are real awareness
 * constructs that hold for any advertiser — and it invents no evidence: each
 * card's default copy states an approach, never a fact about the campaign.
 */
const GENERAL_BRAND_AWARENESS_TAXONOMY = Object.freeze({
  key: "general",
  cards: Object.freeze([
    {
      key: "brand-offering-positioning",
      title: "Brand / offering positioning",
      meta: "Positioning signal",
      signal: "narrative",
      lead: (subject) =>
        `State what ${subject} offers, who it is for, and what sets it apart in one line people can repeat.`,
      accent: "rgba(113, 227, 255, 0.94)",
    },
    {
      key: "audience-community-strategy",
      title: "Audience / community strategy",
      meta: "Trusted distribution",
      signal: "audience",
      lead: () =>
        "Reach priority audiences through the communities, partners, and trusted voices they already follow.",
      accent: "rgba(184, 126, 255, 0.92)",
    },
    {
      key: "timing-occasion-strategy",
      title: "Timing / occasion strategy",
      meta: "Timing design",
      signal: "moment",
      lead: () =>
        "Concentrate pressure on the moments and occasions when this audience is most receptive, rather than spreading it evenly.",
      accent: "rgba(76, 141, 255, 0.96)",
    },
    {
      key: "creator-partner-amplification",
      title: "Creator / partner amplification",
      meta: "Earned narrative",
      signal: "earned",
      lead: () =>
        "Work with creators, partners, newsletters, and relevant publications so credible coverage exists before paid media scales it.",
      accent: "rgba(255, 107, 137, 0.9)",
    },
    {
      key: "social-proof-credibility",
      title: "Social proof / credibility",
      meta: "Proof loop",
      signal: "socialProof",
      lead: () =>
        "Treat reviews, ratings, testimonials, and audience response as proof assets that paid creative can reuse.",
      accent: "rgba(63, 224, 160, 0.92)",
    },
    {
      key: "contextual-cultural-relevance",
      title: "Contextual / cultural relevance",
      meta: "Why now",
      signal: "context",
      lead: (subject) =>
        `Connect ${subject} to the context its audience already recognises, then make that angle easy to encounter repeatedly.`,
      accent: "rgba(255, 206, 107, 0.92)",
    },
  ]),
  signalDefaults: Object.freeze({
    narrative: (subject) =>
      `${subject} needs one clear story people can repeat, then creative executions that carry it across discovery and consideration.`,
    audience:
      "Priority audiences should encounter the campaign through trusted voices and relevant communities before paid retargeting asks for action.",
    moment:
      "Timing should follow the occasions when this audience is most receptive, so pressure lands when attention is available.",
    earned:
      "Credible third-party coverage should come first; Cphere can then scale the strongest proof through paid creative and retargeting.",
    socialProof:
      "Reviews, testimonials, and audience response should be treated as proof assets that paid media can reuse.",
    context:
      "Anchor the campaign in the context its audience already recognises, then use paid media to make that angle easier to encounter repeatedly.",
  }),
});

/*
 * Specialised taxonomies. Adding a vertical is one entry here — the renderer
 * reads the resolved taxonomy and never names a category itself.
 *
 * Theatrical is listed like any other specialisation rather than sitting
 * underneath as the fallback. That is the whole point: it is reachable only by
 * an affirmative "film" classification.
 */
const BRAND_AWARENESS_TAXONOMY_BY_VERTICAL = Object.freeze({
  film: FILM_BRAND_AWARENESS_TAXONOMY,
  retail: RETAIL_BRAND_AWARENESS_TAXONOMY,
});

/**
 * The brand-awareness card set a governed category is entitled to.
 *
 * Fail-closed: anything that is not an affirmative "film" or "retail" — including
 * an absent, empty, or unrecognised value — resolves to the neutral general set.
 */
export function getBrandAwarenessTaxonomy(vertical) {
  return (
    BRAND_AWARENESS_TAXONOMY_BY_VERTICAL[normalizeAdvertiserVertical(vertical)] ||
    GENERAL_BRAND_AWARENESS_TAXONOMY
  );
}

// Category presentation for the Campaign Strategy microsite.
//
// The microsite names the discipline its strategy serves in two places: the
// hero title ("Digital Media Plan + <strategyLabel>") and the action-items
// heading ("Steps to Immediate <impactLabel> Impact"). Both were hard-coded to
// the film vertical's wording, so a Retail advertiser read
// "Theatrical Marketing Strategy" directly above its own retail positioning.
//
// These are PRESENTATION tokens derived from the canonical advertiser vertical.
// Nothing here may key on an individual advertiser or campaign.
//
// `film` is the locked Arts & Entertainment control vertical and keeps its
// established "Theatrical" wording. `general` is the deterministic fallback for
// an absent or unsupported category: it is category-NEUTRAL rather than
// borrowing another vertical's vocabulary, because naming the wrong discipline
// is worse than naming none.
export const CAMPAIGN_STRATEGY_TITLE_PREFIX = "Digital Media Plan";

const NEUTRAL_STRATEGY_PRESENTATION = Object.freeze({
  strategyLabel: "Campaign Strategy",
  impactLabel: "Campaign",
});

const ADVERTISER_VERTICAL_STRATEGY_PRESENTATION = Object.freeze({
  general: NEUTRAL_STRATEGY_PRESENTATION,
  film: Object.freeze({
    strategyLabel: "Theatrical Marketing Strategy",
    impactLabel: "Theatrical",
  }),
  automotive: Object.freeze({
    strategyLabel: "Automotive Marketing Strategy",
    impactLabel: "Automotive",
  }),
  retail: Object.freeze({
    strategyLabel: "Retail Marketing Strategy",
    impactLabel: "Retail",
  }),
  travel_hospitality: Object.freeze({
    strategyLabel: "Travel Marketing Strategy",
    impactLabel: "Travel",
  }),
  financial_services: Object.freeze({
    strategyLabel: "Financial Services Marketing Strategy",
    impactLabel: "Financial Services",
  }),
  crypto_web3: Object.freeze({
    strategyLabel: "Digital Asset Marketing Strategy",
    impactLabel: "Digital Asset",
  }),
  health_wellness: Object.freeze({
    strategyLabel: "Health and Wellness Marketing Strategy",
    impactLabel: "Health and Wellness",
  }),
  healthcare_public_health: Object.freeze({
    strategyLabel: "Healthcare Marketing Strategy",
    impactLabel: "Healthcare",
  }),
  education: Object.freeze({
    strategyLabel: "Education Marketing Strategy",
    impactLabel: "Education",
  }),
  b2b_saas: Object.freeze({
    strategyLabel: "B2B Marketing Strategy",
    impactLabel: "B2B",
  }),
  nonprofit_public_sector: Object.freeze({
    strategyLabel: "Nonprofit Marketing Strategy",
    impactLabel: "Nonprofit",
  }),
  real_estate: Object.freeze({
    strategyLabel: "Real Estate Marketing Strategy",
    impactLabel: "Real Estate",
  }),
  food_beverage: Object.freeze({
    strategyLabel: "Food and Beverage Marketing Strategy",
    impactLabel: "Food and Beverage",
  }),
  cpg: Object.freeze({
    strategyLabel: "CPG Marketing Strategy",
    impactLabel: "CPG",
  }),
  gaming: Object.freeze({
    strategyLabel: "Gaming Marketing Strategy",
    impactLabel: "Gaming",
  }),
  telecom: Object.freeze({
    strategyLabel: "Telecom Marketing Strategy",
    impactLabel: "Telecom",
  }),
  electronics: Object.freeze({
    strategyLabel: "Electronics Marketing Strategy",
    impactLabel: "Electronics",
  }),
});

function resolveAdvertiserVerticalStrategyPresentation(value) {
  return (
    ADVERTISER_VERTICAL_STRATEGY_PRESENTATION[normalizeAdvertiserVertical(value)] ||
    NEUTRAL_STRATEGY_PRESENTATION
  );
}

export function formatAdvertiserVerticalStrategyLabel(value) {
  return resolveAdvertiserVerticalStrategyPresentation(value).strategyLabel;
}

export function formatAdvertiserVerticalImpactLabel(value) {
  return resolveAdvertiserVerticalStrategyPresentation(value).impactLabel;
}

export function formatCampaignStrategyMicrositeTitle(value) {
  return `${CAMPAIGN_STRATEGY_TITLE_PREFIX} + ${formatAdvertiserVerticalStrategyLabel(value)}`;
}

export const VERTICAL_INTELLIGENCE_CONFIG = Object.freeze({
  general: {
    key: "general",
    displayLabel: "General",
    terminology: ["audience response", "platform performance", "campaign optimization"],
    kpiHierarchy: ["spend", "impressions", "clicks", "CTR", "CPC", "CPM", "conversions when present"],
    platformRoleGuidance: {
      search: "high-intent demand capture",
      meta: "audience discovery, social proof, and retargeting",
      instagram: "visual discovery and engagement",
      youtube: "video storytelling and consideration",
      ctv: "upper-funnel reach and attention",
      display: "retargeting, continuity, and reach extension",
      audio: "frequency and recall",
      dooh: "geo-aware awareness",
    },
    narrativeGuidance:
      "Use neutral campaign intelligence language around audience response, platform contribution, efficiency, and qualified traffic. If only media KPIs exist, frame the read around media engagement and traffic efficiency only.",
    optimizationPriorities: [
      "protect efficient platforms",
      "rebalance based on CTR, CPC, CPM, and conversion data when available",
      "separate awareness roles from demand-capture roles",
    ],
    plannerStrategyGuidance:
      "Plan prospecting, retargeting, creative testing, and platform sequencing around the stated campaign objective without assuming vertical-specific outcomes.",
    edenContextGuidance:
      "Answer as a cross-platform strategist using the advertiser objective, live media KPIs, pacing, and platform roles.",
    bannedMismatchPhrases: [
      "box office",
      "ticket sales",
      "showtimes",
      "moviegoers",
      "theatrical release",
      "cinema market",
      "opening weekend",
      "dealer territory",
      "test drive",
      "showroom intent",
      "cart activity",
      "SKU",
      "booking consideration",
      "traveler intent",
      "occupancy",
    ],
    unsupportedClaimGuards: [
      "Do not infer purchases, leads, ticketing, showroom activity, or product-level performance unless those fields are present.",
    ],
  },
  film: {
    key: "film",
    displayLabel: "Film / Entertainment",
    terminology: [
      "theatrical release",
      "awareness build",
      "trailer/video engagement",
      "release-window acceleration",
      "audience discovery",
    ],
    kpiHierarchy: [
      "impressions",
      "video views",
      "completed views",
      "engagements",
      "CTR",
      "CPC",
      "ticketing intent only when present",
    ],
    platformRoleGuidance: {
      youtube: "in-stream video delivery and audience discovery",
      ctv: "connected-TV video delivery during the release window",
      meta: "social discovery and retargeting around title interest",
      instagram: "visual title discovery and engagement",
      tiktok: "short-form discovery and culture signal",
      display: "retargeting and release-message reinforcement",
      dooh: "market-level theatrical awareness",
      cinema: "theatrical context and attendance adjacency",
    },
    narrativeGuidance:
      "Interpret performance as theatrical-release awareness, audience discovery, video engagement, and release-window acceleration when the data supports it.",
    optimizationPriorities: [
      "protect efficient video engagement",
      "scale audience discovery before and during release windows",
      "use completion and engagement data before claiming downstream intent",
    ],
    plannerStrategyGuidance:
      "Build theatrical/media-release logic around awareness, video sequencing, audience discovery, geo-market pressure, and release-window timing.",
    edenContextGuidance:
      "Answer as a theatrical strategist focused on release strategy, awareness, audience engagement, and theatrical performance only when supported.",
    bannedMismatchPhrases: [],
    unsupportedClaimGuards: [
      "Do not claim ticket sales, opening-weekend lift, or cinema performance unless those fields are present.",
    ],
  },
  automotive: {
    key: "automotive",
    displayLabel: "Automotive",
    terminology: [
      "vehicle consideration",
      "in-market shoppers",
      "model interest",
      "dealer engagement",
      "inventory alignment",
      "geo-market performance",
      "lead quality",
    ],
    kpiHierarchy: [
      "leads when present",
      "VDP views when present",
      "phone calls/chat/appointments when present",
      "geo-market performance",
      "CTR",
      "CPC",
      "CPM",
      "impressions",
    ],
    platformRoleGuidance: {
      search: "high-intent shopper capture",
      meta: "model discovery and retargeting",
      instagram: "model discovery and retargeting",
      youtube: "feature storytelling and consideration",
      ctv: "regional awareness and household reach",
      display: "inventory retargeting",
      audio: "commuter and local-market reinforcement",
      dooh: "local dealer-market awareness",
    },
    narrativeGuidance:
      "Prioritize vehicle consideration, in-market shoppers, model interest, dealer-market response, inventory alignment, lead quality, and high-intent shopper behavior only when supported by data.",
    optimizationPriorities: [
      "protect lead quality and high-intent shopper traffic when present",
      "read VDP, calls, chat, appointment, and test-drive intent only when those fields exist",
      "weight spend by dealer-market and model response when available",
    ],
    plannerStrategyGuidance:
      "Include in-market shoppers, model/category segmentation, geo/dealer-market weighting, conquesting, retargeting, finance/lease/purchase messaging, and inventory-aware weighting.",
    edenContextGuidance:
      "Answer around dealer, lead, inventory, model, geo, and shopper intent. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [
      "box office",
      "ticket sales",
      "showtimes",
      "moviegoers",
      "theatrical release",
      "cinema market",
      "opening weekend",
      "cart activity",
      "checkout behavior",
      "SKU",
      "basket size",
      "booking consideration",
      "traveler intent",
      "destination demand",
      "occupancy",
      "room nights",
    ],
    unsupportedClaimGuards: [
      "Do not infer sales, VDP views, calls, test drives, appointments, or leads unless data exists.",
    ],
  },
  retail: {
    key: "retail",
    displayLabel: "Retail / E-Commerce",
    terminology: [
      "ROAS",
      "purchase efficiency",
      "conversion rate",
      "CPA",
      "AOV",
      "cart activity",
      "checkout behavior",
      "product engagement",
      "SKU/category performance",
    ],
    kpiHierarchy: [
      "ROAS when present",
      "purchases/conversions when present",
      "CPA",
      "conversion rate",
      "AOV when present",
      "cart/checkout behavior when present",
      "CTR",
      "CPC",
      "CPM",
    ],
    platformRoleGuidance: {
      search: "high-intent purchase capture",
      meta: "product discovery and retargeting",
      instagram: "product discovery and retargeting",
      tiktok: "impulse discovery and creator-led product interest",
      youtube: "product storytelling and reviews",
      display: "cart recovery and remarketing",
      ctv: "household awareness and seasonal/promo lift",
      email: "retention and repeat purchase support when available",
    },
    narrativeGuidance:
      "Prioritize ROAS, CPA, conversion quality, purchase efficiency, cart recovery, promo responsiveness, SKU/category efficiency, and retargeting depth when those data fields are available.",
    optimizationPriorities: [
      "protect profitable conversion paths when ROAS or purchase data exists",
      "use CPA, conversion rate, cart, checkout, product, and promo signals only when present",
      "separate prospecting from retargeting and retention roles",
    ],
    plannerStrategyGuidance:
      "Include prospecting versus retargeting balance, product/category segmentation, promo cadence, seasonal shopping behavior, cart recovery, new customer acquisition, repeat purchase strategy, and offer/product storytelling.",
    edenContextGuidance:
      "Answer around ROAS, purchase intent, cart, product, promo, and conversion efficiency. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [
      "box office",
      "ticket sales",
      "showtimes",
      "moviegoers",
      "theatrical release",
      "cinema market",
      "opening weekend",
      "dealer territory",
      "vehicle interest",
      "inventory-aligned demand",
      "test drive",
      "showroom intent",
      "booking consideration",
      "traveler intent",
      "destination demand",
      "occupancy",
      "room nights",
    ],
    unsupportedClaimGuards: [
      "Do not infer purchases, carts, checkout activity, ROAS, or product-level performance unless data exists.",
    ],
  },
  travel_hospitality: {
    key: "travel_hospitality",
    displayLabel: "Travel / Hospitality",
    terminology: [
      "destination demand",
      "traveler intent",
      "booking consideration",
      "guest engagement",
      "origin-market response",
      "seasonal travel windows",
      "property or route interest",
    ],
    kpiHierarchy: [
      "bookings/reservations when present",
      "booking inquiries when present",
      "qualified traffic",
      "destination or property page engagement when present",
      "CTR",
      "CPC",
      "CPM",
      "impressions",
    ],
    platformRoleGuidance: {
      search: "high-intent trip, destination, or booking demand capture",
      meta: "destination discovery, social proof, and retargeting",
      instagram: "visual destination and property inspiration",
      tiktok: "short-form destination discovery and creator-led travel interest",
      youtube: "destination, property, route, or experience storytelling",
      ctv: "premium reach for seasonal travel inspiration and destination awareness",
      display: "site visitor, itinerary, and booking-path retargeting",
      audio: "commuter and lifestyle reach for destination recall",
      dooh: "origin-market or destination-market awareness",
      email: "loyalty, offer, and returning guest support when available",
    },
    narrativeGuidance:
      "Prioritize destination demand, traveler intent, booking consideration, guest engagement, property or route interest, origin-market response, and seasonal travel timing only when supported by data.",
    optimizationPriorities: [
      "protect efficient traveler-intent traffic when present",
      "use booking, reservation, occupancy, loyalty, and property-level signals only when those fields exist",
      "separate destination inspiration from booking-path retargeting and loyalty roles",
    ],
    plannerStrategyGuidance:
      "Include origin-market weighting, destination or property storytelling, seasonal travel windows, trip-planning intent, package or offer cadence, booking-path retargeting, loyalty audiences, and guest-experience messaging.",
    edenContextGuidance:
      "Answer around destination demand, traveler intent, booking consideration, guest engagement, origin markets, and seasonal timing. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [
      "box office",
      "ticket sales",
      "showtimes",
      "moviegoers",
      "theatrical release",
      "cinema market",
      "opening weekend",
      "dealer territory",
      "vehicle interest",
      "inventory-aligned demand",
      "test drive",
      "showroom intent",
      "cart activity",
      "checkout behavior",
      "SKU",
      "product-level performance",
    ],
    unsupportedClaimGuards: [
      "Do not infer bookings, reservations, occupancy, room nights, route sales, or loyalty activity unless data exists.",
    ],
  },
  financial_services: {
    key: "financial_services",
    displayLabel: "Financial Services",
    terminology: ["account acquisition", "application starts", "qualified leads", "audience qualification", "consideration and trust", "lead quality", "retention and lifetime value"],
    kpiHierarchy: ["application starts when present", "qualified leads when present", "account openings when present", "cost per qualified lead", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent product and application demand capture",
      meta: "audience qualification, education, and retargeting",
      instagram: "brand trust and product education",
      youtube: "product explainers and consideration storytelling",
      ctv: "brand trust and household reach",
      display: "application-path retargeting and continuity",
      audio: "commuter reach and brand recall",
      dooh: "local-market brand presence",
    },
    narrativeGuidance:
      "Prioritize consideration, trust, account acquisition, application starts, lead quality, and lifetime value only when supported by data. All claims stay compliance-safe: no guarantees, no performance promises.",
    optimizationPriorities: [
      "protect qualified-lead efficiency when lead data exists",
      "read application starts, account openings, and retention only when those fields exist",
      "separate education/trust roles from application-capture roles",
    ],
    plannerStrategyGuidance:
      "Include audience qualification, education-first sequencing, application-path retargeting, compliance-review-friendly messaging, lead-quality weighting, and retention/lifetime-value framing.",
    edenContextGuidance:
      "Answer around applications, qualified leads, account acquisition, trust, and compliance-safe framing. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "guaranteed returns", "risk-free", "get rich", "guaranteed yield", "cart activity", "test drive", "occupancy"],
    unsupportedClaimGuards: [
      "Do not infer applications, account openings, leads, or retention unless data exists.",
      "Never state or imply guaranteed returns, rates, approvals, or financial outcomes.",
    ],
  },
  crypto_web3: {
    key: "crypto_web3",
    displayLabel: "Blockchain / Crypto / Web3",
    terminology: ["community growth", "wallet and account intent", "compliant onboarding", "product understanding", "ecosystem participation", "education and trust", "conversion quality"],
    kpiHierarchy: ["verified sign-ups when present", "wallet or account activations when present", "qualified onboarding when present", "community growth when present", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent product and onboarding demand capture",
      meta: "community discovery, education, and retargeting (policy-compliant)",
      instagram: "product understanding and community culture",
      youtube: "explainers, education, and trust storytelling",
      ctv: "brand legitimacy and household reach",
      display: "onboarding-path retargeting and continuity",
      audio: "brand recall with compliant messaging",
      dooh: "market-level brand presence",
    },
    narrativeGuidance:
      "Prioritize community growth, onboarding intent, product understanding, and education-led trust only when supported by data. Treat every claim as compliance-sensitive: never promise returns, price movement, or yield.",
    optimizationPriorities: [
      "protect qualified onboarding efficiency when sign-up data exists",
      "read wallet/account activations and community growth only when those fields exist",
      "separate education roles from onboarding-capture roles; keep all claims compliance-safe",
    ],
    plannerStrategyGuidance:
      "Include education-first sequencing, community-building, compliant onboarding paths, platform policy constraints for crypto advertising, retargeting on product understanding, and conversion-quality weighting.",
    edenContextGuidance:
      "Answer around community growth, onboarding, wallet/account intent, and education — compliance-sensitive, never speculative. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "guaranteed returns", "guaranteed yield", "moon", "price prediction", "investment promise", "risk-free", "cart activity", "test drive"],
    unsupportedClaimGuards: [
      "Do not infer wallet activations, sign-ups, deposits, or community growth unless data exists.",
      "Never state or imply guaranteed returns, guaranteed yield, price predictions, or investment outcomes.",
    ],
  },
  health_wellness: {
    key: "health_wellness",
    displayLabel: "Health / Wellness",
    terminology: ["wellness awareness", "booking and appointment intent", "membership interest", "routine building", "community trust", "local demand", "lifestyle improvement"],
    kpiHierarchy: ["bookings or appointments when present", "membership sign-ups when present", "offer redemptions when present", "qualified traffic", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent local wellness and booking demand capture",
      meta: "community discovery, social proof, and retargeting",
      instagram: "lifestyle inspiration and routine storytelling",
      youtube: "routine, class, and transformation-safe storytelling",
      ctv: "lifestyle brand awareness and household reach",
      display: "booking-path retargeting and offer continuity",
      audio: "routine-building reach and recall",
      dooh: "local studio/location awareness",
    },
    narrativeGuidance:
      "Prioritize wellness awareness, booking/appointment intent, membership interest, routine building, and community trust only when supported by data. Frame outcomes responsibly: no medical claims, no cure/treatment language, no guaranteed results.",
    optimizationPriorities: [
      "protect efficient booking/membership paths when those fields exist",
      "read offers, redemptions, and membership signals only when present",
      "separate lifestyle inspiration from booking-path retargeting",
    ],
    plannerStrategyGuidance:
      "Include local-market weighting, class/offer cadence, booking-path retargeting, membership funnels, community trust storytelling, and responsible outcome framing.",
    edenContextGuidance:
      "Answer around bookings, memberships, offers, local demand, and community trust with responsible wellness framing. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cure", "guaranteed results", "medical claim", "cart activity", "test drive", "dealer territory"],
    unsupportedClaimGuards: [
      "Do not infer bookings, memberships, or redemptions unless data exists.",
      "Never make medical claims, cure/treatment claims, or guaranteed health outcomes.",
    ],
  },
  healthcare_public_health: {
    key: "healthcare_public_health",
    displayLabel: "Healthcare / Public Health",
    terminology: ["patient education", "service-line awareness", "appointment intent", "access and prevention", "community trust", "screening awareness", "public health outreach"],
    kpiHierarchy: ["appointment requests when present", "screening sign-ups when present", "qualified information engagement", "reach in priority communities", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent care, condition, and appointment demand capture",
      meta: "community education, access outreach, and retargeting (policy-safe)",
      instagram: "trust storytelling and service-line awareness",
      youtube: "patient education and prevention storytelling",
      ctv: "community-level trust and household reach",
      display: "appointment-path retargeting and education continuity",
      audio: "community reach for prevention and access messaging",
      dooh: "facility and public-health outreach presence",
    },
    narrativeGuidance:
      "Prioritize patient education, service-line awareness, appointment intent, access, prevention, and community trust only when supported by data. Messaging is compliance-sensitive and responsible: no unsupported medical claims, no aggressive sales language.",
    optimizationPriorities: [
      "protect appointment and screening paths when those fields exist",
      "read patient engagement and access signals only when present",
      "separate education/outreach roles from appointment-capture roles",
    ],
    plannerStrategyGuidance:
      "Include service-line segmentation, community/priority-population weighting, prevention and screening outreach, appointment-path retargeting, and compliance-sensitive creative review.",
    edenContextGuidance:
      "Answer around patient education, service-line awareness, appointments, access, and prevention with compliance-sensitive framing. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cure", "guaranteed outcomes", "cart activity", "SKU", "test drive", "occupancy"],
    unsupportedClaimGuards: [
      "Do not infer appointments, screenings, or patient outcomes unless data exists.",
      "Never make unsupported medical claims or promise clinical outcomes.",
    ],
  },
  education: {
    key: "education",
    displayLabel: "Education",
    terminology: ["program awareness", "enrollment interest", "applications", "information sessions", "campus visits", "admissions funnel", "student and parent audiences"],
    kpiHierarchy: ["applications when present", "enrollment inquiries when present", "information-session sign-ups when present", "qualified traffic", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent program and enrollment demand capture",
      meta: "program discovery, social proof, and retargeting",
      instagram: "campus and student-life storytelling",
      youtube: "program explainers and outcomes-safe storytelling",
      ctv: "brand and program awareness with household reach",
      display: "application-path retargeting and deadline continuity",
      audio: "commuter and parent-audience reach",
      dooh: "local catchment-area awareness",
    },
    narrativeGuidance:
      "Prioritize program awareness, enrollment interest, applications, information sessions, and admissions-funnel movement only when supported by data.",
    optimizationPriorities: [
      "protect application-path efficiency when application data exists",
      "read inquiries, sessions, and visits only when those fields exist",
      "separate program awareness from application-capture roles",
    ],
    plannerStrategyGuidance:
      "Include program/term segmentation, deadline-driven cadence, student and parent audience split, information-session promotion, campus-visit support, and application-path retargeting.",
    edenContextGuidance:
      "Answer around programs, enrollment, applications, sessions, and admissions-funnel stages. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cart activity", "SKU", "test drive", "occupancy", "patient"],
    unsupportedClaimGuards: [
      "Do not infer applications, enrollments, or visits unless data exists.",
      "Never promise admission, outcomes, or career results.",
    ],
  },
  b2b_saas: {
    key: "b2b_saas",
    displayLabel: "B2B / SaaS",
    terminology: ["pipeline influence", "qualified leads", "demo requests", "account-based audiences", "buyer committees", "product education", "sales enablement"],
    kpiHierarchy: ["qualified leads when present", "demo requests when present", "pipeline influence when present", "target-account engagement when present", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent solution and category demand capture",
      meta: "audience education and retargeting support",
      instagram: "brand credibility and product storytelling",
      youtube: "product demos and category education",
      ctv: "category authority and decision-maker reach",
      display: "account-based retargeting and continuity",
      audio: "decision-maker commute reach",
      dooh: "event- and market-level brand presence",
    },
    narrativeGuidance:
      "Prioritize pipeline influence, qualified leads, demo requests, account-based engagement, buyer-committee coverage, and lifecycle stage only when supported by data.",
    optimizationPriorities: [
      "protect demo-path and qualified-lead efficiency when lead data exists",
      "read pipeline and account engagement only when those fields exist",
      "separate category education from demo-capture roles",
    ],
    plannerStrategyGuidance:
      "Include account-based audience definitions, buyer-committee coverage, product education sequencing, demo-path retargeting, sales-enablement alignment, and lifecycle-stage weighting.",
    edenContextGuidance:
      "Answer around pipeline, qualified leads, demos, target accounts, and lifecycle stages. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cart activity", "SKU", "test drive", "occupancy", "patient"],
    unsupportedClaimGuards: [
      "Do not infer leads, demos, pipeline, or closed-won revenue unless data exists.",
    ],
  },
  nonprofit_public_sector: {
    key: "nonprofit_public_sector",
    displayLabel: "Nonprofit / Public Sector",
    terminology: ["community impact", "awareness and education", "donations where applicable", "volunteer interest", "civic action", "public service", "stakeholder trust"],
    kpiHierarchy: ["donations when present", "volunteer sign-ups when present", "civic-action completions when present", "qualified engagement", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent cause, program, and service demand capture",
      meta: "community education, supporter discovery, and retargeting",
      instagram: "impact storytelling and community trust",
      youtube: "mission and impact storytelling",
      ctv: "community-level awareness and trust",
      display: "supporter-path retargeting and continuity",
      audio: "community reach for public-service messaging",
      dooh: "community and civic-space presence",
    },
    narrativeGuidance:
      "Prioritize awareness, education, community impact, donations (only when fundraising is explicit), volunteer interest, and civic action only when supported by data. Avoid hard-sell phrasing.",
    optimizationPriorities: [
      "protect supporter-path efficiency when donation/volunteer data exists",
      "read civic-action and engagement signals only when present",
      "separate awareness/education from supporter-conversion roles",
    ],
    plannerStrategyGuidance:
      "Include community weighting, impact storytelling, supporter-path retargeting, volunteer funnels, civic-action sequencing, and trust-first messaging.",
    edenContextGuidance:
      "Answer around community impact, supporters, volunteers, and civic action with public-service framing. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cart activity", "SKU", "test drive", "occupancy"],
    unsupportedClaimGuards: [
      "Do not infer donations, volunteer sign-ups, or civic actions unless data exists.",
    ],
  },
  real_estate: {
    key: "real_estate",
    displayLabel: "Real Estate",
    terminology: ["listing awareness", "property inquiries", "open houses", "neighborhood interest", "buyer and seller intent", "local market demand", "lead quality"],
    kpiHierarchy: ["property inquiries when present", "open-house sign-ups when present", "qualified leads when present", "local-market engagement", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent listing, neighborhood, and agent demand capture",
      meta: "listing discovery, neighborhood storytelling, and retargeting",
      instagram: "property and neighborhood visual storytelling",
      youtube: "property tours and neighborhood guides",
      ctv: "local-market brand presence and household reach",
      display: "inquiry-path retargeting and listing continuity",
      audio: "commuter reach for local-market presence",
      dooh: "neighborhood- and market-level awareness",
    },
    narrativeGuidance:
      "Prioritize listing awareness, property inquiries, open houses, neighborhood interest, buyer/seller intent, and lead quality only when supported by data.",
    optimizationPriorities: [
      "protect inquiry-path and lead-quality efficiency when lead data exists",
      "read open-house and appointment signals only when those fields exist",
      "separate neighborhood/brand awareness from inquiry-capture roles",
    ],
    plannerStrategyGuidance:
      "Include neighborhood/geo weighting, listing segmentation, open-house cadence, inquiry-path retargeting, buyer vs seller audience split, and community positioning.",
    edenContextGuidance:
      "Answer around listings, inquiries, open houses, neighborhoods, and buyer/seller intent. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "guaranteed returns", "cart activity", "SKU", "patient", "occupancy"],
    unsupportedClaimGuards: [
      "Do not infer inquiries, showings, appointments, or sales unless data exists.",
      "Never promise price appreciation or investment outcomes.",
    ],
  },
  food_beverage: {
    key: "food_beverage",
    displayLabel: "Food / Beverage / Restaurants",
    terminology: ["reservations", "orders and delivery", "foot traffic", "menu awareness", "local demand", "loyalty and offers", "dining occasions"],
    kpiHierarchy: ["reservations when present", "orders when present", "offer redemptions when present", "local qualified traffic", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent local dining and ordering demand capture",
      meta: "menu discovery, social proof, and retargeting",
      instagram: "craveable menu and occasion storytelling",
      youtube: "menu, occasion, and brand storytelling",
      ctv: "local brand awareness and occasion priming",
      display: "order/reservation-path retargeting and offer continuity",
      audio: "commute-time occasion reach",
      dooh: "near-location and trade-area awareness",
    },
    narrativeGuidance:
      "Prioritize reservations, orders, foot traffic, menu awareness, offers, loyalty, and dining occasions only when supported by data.",
    optimizationPriorities: [
      "protect reservation/order-path efficiency when those fields exist",
      "read redemptions, loyalty, and delivery signals only when present",
      "separate occasion/menu awareness from order-capture roles",
    ],
    plannerStrategyGuidance:
      "Include trade-area weighting, daypart and occasion cadence, menu/new-item storytelling, offer and loyalty support, reservation/order-path retargeting, and delivery/takeout coverage where applicable.",
    edenContextGuidance:
      "Answer around reservations, orders, menu interest, local demand, and offers. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "SKU", "test drive", "patient", "occupancy"],
    unsupportedClaimGuards: [
      "Do not infer reservations, orders, redemptions, or visits unless data exists.",
    ],
  },
  cpg: {
    key: "cpg",
    displayLabel: "CPG",
    terminology: ["product discovery", "category awareness", "trial rates", "repeat purchase", "shopper intent", "retailer availability", "retail velocity", "basket lift"],
    kpiHierarchy: ["purchases when present", "trial rates when present", "repeat purchase when present", "retail velocity when present", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent product and where-to-buy demand capture",
      meta: "product discovery, social proof, and retargeting",
      instagram: "product and usage-occasion storytelling",
      tiktok: "trial-driving discovery and creator-led product interest",
      youtube: "product storytelling and usage education",
      ctv: "category awareness and household reach",
      display: "where-to-buy retargeting and offer continuity",
      audio: "shopping-trip priming and recall",
      dooh: "near-retailer and trade-area awareness",
    },
    narrativeGuidance:
      "Prioritize product discovery, category awareness, trial, repeat purchase, shopper intent, retailer availability, retail velocity, and basket lift only when supported by data.",
    optimizationPriorities: [
      "protect trial and repeat-purchase efficiency when purchase data exists",
      "read velocity, availability, and basket signals only when those fields exist",
      "separate category awareness from trial/repeat-capture roles",
    ],
    plannerStrategyGuidance:
      "Include category/occasion segmentation, retailer-availability alignment, trial-driving offers, repeat-purchase support, where-to-buy pathing, and trade-area weighting.",
    edenContextGuidance:
      "Answer around product discovery, trial, repeat purchase, shopper intent, and retailer availability. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "test drive", "dealer territory", "patient", "occupancy"],
    unsupportedClaimGuards: [
      "Do not infer purchases, trial, repeat rates, velocity, or basket lift unless data exists.",
    ],
  },
  gaming: {
    key: "gaming",
    displayLabel: "Gaming",
    terminology: ["player acquisition", "installs", "player retention", "engagement depth", "community growth", "esports and creator moments", "ROAS where measurable"],
    kpiHierarchy: ["installs when present", "ROAS when present", "player retention when present", "engagement depth when present", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent title and install demand capture",
      meta: "player discovery, community proof, and retargeting",
      instagram: "gameplay culture and creator moments",
      tiktok: "short-form gameplay discovery and community culture",
      youtube: "gameplay showcases, creator content, and consideration",
      ctv: "franchise awareness and household reach",
      display: "install-path retargeting and continuity",
      audio: "session-adjacent reach and recall",
      dooh: "launch-moment and event awareness",
    },
    narrativeGuidance:
      "Prioritize player acquisition, installs, retention, engagement depth, community response, and esports or creator moments only when supported by data. Read ROAS only when monetization data exists.",
    optimizationPriorities: [
      "protect efficient install paths when install data exists",
      "read retention, session, and monetization signals only when those fields exist",
      "separate franchise awareness from install-capture roles",
    ],
    plannerStrategyGuidance:
      "Include genre and platform segmentation, launch and content-update timing, creator and community amplification, install-path retargeting, re-engagement of lapsed players, and ROAS weighting where monetization data exists.",
    edenContextGuidance:
      "Answer around installs, player acquisition, retention, engagement, and community response. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cart activity", "SKU", "test drive", "occupancy", "patient"],
    unsupportedClaimGuards: [
      "Do not infer installs, sign-ups, retention, sessions, or in-game monetization unless data exists.",
    ],
  },
  telecom: {
    key: "telecom",
    displayLabel: "Telecom",
    terminology: ["plan adoption", "coverage confidence", "switching intent", "line activations", "household and business connectivity", "wireless, fiber, and internet plans", "upgrade and bundle interest"],
    kpiHierarchy: ["line activations when present", "plan sign-ups when present", "qualified coverage checks when present", "qualified traffic", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent plan, coverage, and switching demand capture",
      meta: "plan discovery, offer education, and retargeting",
      instagram: "network brand and offer storytelling",
      youtube: "network story and plan explainers",
      ctv: "household reach for network and bundle awareness",
      display: "activation-path retargeting and offer continuity",
      audio: "commuter reach and brand recall",
      dooh: "local coverage-market presence",
    },
    narrativeGuidance:
      "Prioritize plan adoption, switching intent, coverage confidence, line activations, and household or business connectivity demand only when supported by data.",
    optimizationPriorities: [
      "protect activation-path efficiency when sign-up data exists",
      "read coverage checks, upgrades, and bundle signals only when those fields exist",
      "separate network-brand awareness from plan-capture roles",
    ],
    plannerStrategyGuidance:
      "Include coverage-market weighting, switcher conquest messaging, plan and bundle cadence, activation-path retargeting, household versus business segmentation, and upgrade-cycle timing.",
    edenContextGuidance:
      "Answer around plan adoption, switching, coverage, activations, and connectivity demand. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cart activity", "SKU", "test drive", "occupancy", "patient"],
    unsupportedClaimGuards: [
      "Do not infer activations, plan sign-ups, coverage checks, or churn unless data exists.",
      "Never promise network performance, speeds, or coverage quality.",
    ],
  },
  electronics: {
    key: "electronics",
    displayLabel: "Electronics",
    terminology: ["device consideration", "product launch windows", "feature comparison", "purchase intent", "retail and ecommerce consideration", "upgrade-cycle timing", "review and spec research"],
    kpiHierarchy: ["purchases when present", "pre-orders when present", "purchase-intent signals when present", "product-page engagement when present", "CTR", "CPC", "CPM", "impressions"],
    platformRoleGuidance: {
      search: "high-intent device and where-to-buy demand capture",
      meta: "device discovery, social proof, and retargeting",
      instagram: "product design and lifestyle storytelling",
      tiktok: "short-form product discovery and creator reviews",
      youtube: "reviews, feature comparison, and consideration storytelling",
      ctv: "launch awareness and household reach",
      display: "purchase-path retargeting and launch continuity",
      audio: "launch recall and brand presence",
      dooh: "launch-moment and near-retail awareness",
    },
    narrativeGuidance:
      "Prioritize device consideration, feature comparison, purchase intent, launch-window response, and retail or ecommerce consideration only when supported by data.",
    optimizationPriorities: [
      "protect purchase-path efficiency when purchase data exists",
      "read pre-orders, registrations, and retailer signals only when those fields exist",
      "separate launch awareness from purchase-capture roles",
    ],
    plannerStrategyGuidance:
      "Include launch-window sequencing, feature and comparison storytelling, review and creator amplification, purchase-path retargeting, retailer and ecommerce alignment, and upgrade-cycle timing.",
    edenContextGuidance:
      "Answer around device consideration, feature comparison, purchase intent, launches, and retail or ecommerce consideration. If only media KPIs exist, explicitly limit the read to media engagement and traffic efficiency.",
    bannedMismatchPhrases: [...ENTERTAINMENT_BANNED_BASE, "cart activity", "SKU", "test drive", "occupancy", "patient"],
    unsupportedClaimGuards: [
      "Do not infer purchases, pre-orders, registrations, or retail sell-through unless data exists.",
    ],
  },
});

export function getVerticalIntelligenceConfig(value) {
  const vertical = normalizeAdvertiserVertical(value);
  return VERTICAL_INTELLIGENCE_CONFIG[vertical] || VERTICAL_INTELLIGENCE_CONFIG.general;
}

export function serializeVerticalIntelligence(value) {
  const config = getVerticalIntelligenceConfig(value);
  return {
    key: config.key,
    label: config.displayLabel,
    terminology: config.terminology,
    kpiHierarchy: config.kpiHierarchy,
    platformRoleGuidance: config.platformRoleGuidance,
    narrativeGuidance: config.narrativeGuidance,
    optimizationPriorities: config.optimizationPriorities,
    plannerStrategyGuidance: config.plannerStrategyGuidance,
    edenContextGuidance: config.edenContextGuidance,
    bannedMismatchPhrases: config.bannedMismatchPhrases,
    unsupportedClaimGuards: config.unsupportedClaimGuards,
  };
}

export function getVerticalPlatformRole(vertical, platformKey) {
  const config = getVerticalIntelligenceConfig(vertical);
  const key = String(platformKey || "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
  if (key.includes("search")) return config.platformRoleGuidance.search;
  if (key.includes("facebook") || key.includes("meta")) return config.platformRoleGuidance.meta;
  if (key.includes("instagram")) return config.platformRoleGuidance.instagram;
  if (key.includes("youtube")) return config.platformRoleGuidance.youtube;
  if (key.includes("tiktok")) return config.platformRoleGuidance.tiktok;
  if (key.includes("ctv")) return config.platformRoleGuidance.ctv;
  if (key.includes("display")) return config.platformRoleGuidance.display;
  if (key.includes("audio")) return config.platformRoleGuidance.audio;
  if (key.includes("dooh")) return config.platformRoleGuidance.dooh;
  if (key.includes("email")) return config.platformRoleGuidance.email;
  return config.narrativeGuidance;
}

// Signature scrubs for the expanded verticals: each vertical's distinctive
// terms → neutral equivalents, applied to every other non-film vertical.
// Film is the locked Arts & Entertainment control: its sanitize behavior is
// frozen and receives none of these.
const EXPANDED_VERTICAL_SIGNATURE_SCRUBS = {
  financial_services: [
    [/\baccount acquisition\b/gi, "customer acquisition"],
    [/\bapplication starts?\b/gi, "sign-up starts"],
  ],
  crypto_web3: [
    [/\bwallet activations?\b/gi, "account activations"],
    [/\bwallets?\b/gi, "accounts"],
    [/\bweb3\b/gi, "digital ecosystem"],
    [/\bon-chain\b/gi, "platform-level"],
  ],
  health_wellness: [
    [/\bmembership interest\b/gi, "subscription interest"],
    [/\broutine building\b/gi, "habit building"],
  ],
  healthcare_public_health: [
    [/\bpatient education\b/gi, "customer education"],
    [/\bpatients? and caregivers?\b/gi, "customers"],
    [/\bpatients?\b/gi, "customers"],
    [/\bservice-line\b/gi, "service-category"],
    [/\bpublic health outreach\b/gi, "community outreach"],
  ],
  education: [
    [/\benrollment interest\b/gi, "sign-up interest"],
    [/\benrollments?\b/gi, "sign-ups"],
    [/\badmissions funnel\b/gi, "conversion funnel"],
    [/\bcampus visits?\b/gi, "location visits"],
    [/\binformation sessions?\b/gi, "info events"],
  ],
  b2b_saas: [
    [/\bpipeline influence\b/gi, "demand influence"],
    [/\bpipeline\b/gi, "qualified demand"],
    [/\bdemo requests?\b/gi, "inquiries"],
    [/\baccount-based\b/gi, "audience-based"],
    [/\bbuyer committees?\b/gi, "decision makers"],
    [/\bsales enablement\b/gi, "sales support"],
  ],
  nonprofit_public_sector: [
    [/\bdonations?\b/gi, "contributions"],
    [/\bvolunteer interest\b/gi, "participation interest"],
    [/\bvolunteers?\b/gi, "participants"],
    [/\bcivic action\b/gi, "community action"],
  ],
  real_estate: [
    [/\bopen houses?\b/gi, "local events"],
    [/\blisting awareness\b/gi, "offer awareness"],
    [/\blistings?\b/gi, "offerings"],
    [/\bproperty inquiries\b/gi, "inquiries"],
    [/\bneighborhood interest\b/gi, "local interest"],
  ],
  food_beverage: [
    [/\breservations?\b/gi, "appointments"],
    [/\bmenu awareness\b/gi, "offer awareness"],
    [/\bdining occasions?\b/gi, "purchase occasions"],
  ],
  cpg: [
    [/\bretail velocity\b/gi, "sales velocity"],
    [/\bretailer availability\b/gi, "market availability"],
    [/\bbasket lift\b/gi, "order-value lift"],
    [/\btrial rates?\b/gi, "first-conversion rates"],
  ],
  gaming: [
    [/\bplayer acquisition\b/gi, "customer acquisition"],
    [/\bplayer retention\b/gi, "customer retention"],
    [/\bplayers?\b/gi, "customers"],
    [/\binstalls?\b/gi, "sign-ups"],
    [/\besports\b/gi, "fan communities"],
  ],
  telecom: [
    [/\bplan adoption\b/gi, "service adoption"],
    [/\bswitching intent\b/gi, "competitive intent"],
    [/\bline activations?\b/gi, "account activations"],
    [/\bcoverage confidence\b/gi, "availability confidence"],
  ],
  electronics: [
    [/\bdevice consideration\b/gi, "product consideration"],
    [/\bdevice shoppers?\b/gi, "product shoppers"],
    [/\bfeature comparison\b/gi, "option comparison"],
    [/\bspec research(?:ers)?\b/gi, "product research"],
  ],
};

// Terms a vertical legitimately owns even though another vertical's scrub (or
// a legacy scrub block) would rewrite them.
const VERTICAL_NATIVE_KEEP_TERMS = {
  travel_hospitality: ["reservation"],
  health_wellness: ["booking"],
  food_beverage: ["booking"],
  cpg: ["purchase"],
  financial_services: ["wallet"],
  gaming: ["roas"],
  electronics: ["purchase"],
};

function nativeKeepSet(verticalKey) {
  return new Set(VERTICAL_NATIVE_KEEP_TERMS[verticalKey] || []);
}

export function sanitizeVerticalIntelligenceCopy(value, vertical) {
  const config = getVerticalIntelligenceConfig(vertical);
  const keep = nativeKeepSet(config.key);
  if (typeof value !== "string") return value;
  let text = value.replace(/\bcross-channel\b/gi, "cross-platform");
  text = text.replace(/\bcanonical\b|\bfallback\b|\bresolver\b|raw spend|\bsnapshot\b|\bcache\b|\brollup\b/gi, "campaign data");
  if (config.key !== "film") {
    text = text
      .replace(/\bbox office\b/gi, "downstream performance")
      .replace(/\bticket sales\b/gi, "qualified response")
      .replace(/\bshowtimes?\b/gi, "local availability")
      .replace(/\bmoviegoers?\b/gi, "target audiences")
      .replace(/\btheatrical release\b/gi, "campaign flight")
      .replace(/\bcinema market\b/gi, "market response")
      .replace(/\bopening weekend\b/gi, "launch window")
      .replace(/\btrailer\b/gi, "video creative")
      .replace(/\btheatrical\b/gi, "market")
      .replace(/\bcinema\b/gi, "market");
  }
  if (config.key === "retail") {
    text = text
      .replace(/\bdealer territory\b/gi, "market area")
      .replace(/\bvehicle interest\b/gi, "product interest")
      .replace(/\binventory-aligned demand\b/gi, "product-aligned demand")
      .replace(/\btest-drive intent\b/gi, "high-intent shopper behavior")
      .replace(/\bshowroom intent\b/gi, "store intent");
  }
  if (config.key !== "automotive") {
    text = text
      .replace(/\bVDP views?\b/gi, "site views")
      .replace(/\bvehicle detail page views?\b/gi, "site detail views")
      .replace(/\bdealer-market\b/gi, "market")
      .replace(/\bdealer engagement\b/gi, "local engagement")
      .replace(/\bdealer\b/gi, "local market")
      .replace(/\btest[-\s]?drive intent\b/gi, "high-intent behavior")
      .replace(/\btest[-\s]?drives?\b/gi, "high-intent actions")
      .replace(/\bshowroom intent\b/gi, "local intent")
      .replace(/\bshowroom\b/gi, "local market")
      .replace(/\binventory alignment\b/gi, "offer alignment")
      .replace(/\binventory-aware\b/gi, "offer-aware")
      .replace(/\bmodel interest\b/gi, "audience interest");
  }
  if (config.key !== "retail") {
    if (!keep.has("roas")) {
      text = text.replace(/\bROAS\b/g, "return signal");
    }
    text = text
      .replace(/\bcart activity\b/gi, "site activity")
      .replace(/\bcart recovery\b/gi, "retargeting")
      .replace(/\bcarts?\b/gi, "site actions")
      .replace(/\bcheckout behavior\b/gi, "conversion-path behavior")
      .replace(/\bcheckout\b/gi, "conversion path")
      .replace(/\bSKU\/category performance\b/gi, "offer performance")
      .replace(/\bSKU\b/gi, "offer")
      .replace(/\bAOV\b/g, "order-value signal")
      .replace(/\bproduct-level performance\b/gi, "offer-level performance");
    if (!keep.has("purchase")) {
      text = text
        .replace(/\bpurchase efficiency\b/gi, "response efficiency")
        .replace(/\bpurchases?\b/gi, "conversions");
    }
  }
  if (config.key !== "travel_hospitality" && config.key !== "film") {
    // Travel signature scrub — completes the sanitizer symmetry. Film is the
    // locked Arts & Entertainment control vertical and keeps its behavior.
    text = text
      .replace(/\broom nights?\b/gi, "conversion volume")
      .replace(/\boccupancy\b/gi, "demand")
      .replace(/\btraveler intent\b/gi, "audience intent")
      .replace(/\btravelers?\b/gi, "audiences")
      .replace(/\bdestination demand\b/gi, "market demand")
      .replace(/\bitinerar(?:y|ies)\b/gi, "journey")
      .replace(/\bguest engagement\b/gi, "customer engagement");
    if (!keep.has("booking")) {
      text = text
        .replace(/\bbooking consideration\b/gi, "conversion consideration")
        .replace(/\bbooking[-\s]path\b/gi, "conversion-path")
        .replace(/\bbookings?\b/gi, "conversions");
    }
  }
  if (config.key !== "film") {
    // Expanded-vertical signature scrubs: every non-film vertical except the
    // owning vertical gets the owner's distinctive terms neutralized, minus
    // terms the current vertical natively keeps.
    for (const [owner, replacements] of Object.entries(EXPANDED_VERTICAL_SIGNATURE_SCRUBS)) {
      if (owner === config.key) continue;
      for (const [pattern, replacement] of replacements) {
        if (keep.has("reservation") && pattern.source === "\\breservations?\\b") continue;
        if (keep.has("wallet") && pattern.source === "\\bwallets?\\b") continue;
        text = text.replace(pattern, replacement);
      }
    }
  }
  return text.replace(/\s+/g, " ").trim();
}
